/* eslint-disable no-nested-ternary */
import './InputTextWithI18n.css';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Input, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import Dialog from '../Dialog';
import Icon from '../Icon';
import I18nKeySelect from './I18nKeySelect';

// Trigger onChange when onBlur
function InputTextWithI18n(props) {
  const {
    id,
    value: valueProp,
    i18nInput,
    onChange,
    onI18nChange,
    title,
    placeholder,
    disabled,
    readonly,
    maxLength,
    emptyAsUndefined = true,
    notAllowedRegex,
    onFinishInput,
    className,
    titleClassName,
    inputClassName,
    style,
    i18nSelectorProps,
  } = props;

  const { t } = useTranslation(['icp-form-designer']);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(valueProp);
  const i18nKeySelectRef = useRef();

  useEffect(() => {
    setValue(typeof valueProp === 'number' ? String(valueProp) : valueProp);
  }, [valueProp]);

  const handleChange = (event) => {
    if (notAllowedRegex) {
      setValue(event.target.value.replace(notAllowedRegex, ''));
    } else {
      setValue(event.target.value);
    }
  };

  const handleBlur = () => {
    let newValue = value?.trim();

    if (emptyAsUndefined && newValue === '') {
      newValue = undefined;
    }

    onFinishInput?.(newValue);

    if (newValue === valueProp) {
      return;
    }

    const result = onChange(id, newValue);

    // after trim
    if (newValue !== value) {
      setValue(newValue);
    }

    if (result instanceof Promise) {
      result.catch((err) => {
        console.error(err);
        setValue(valueProp);
      });
    }
  };

  const txtInput = (
    <Input
      value={value ?? ''}
      placeholder={placeholder ?? ''}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={maxLength}
    />
  );

  const txtInputWithI18nInput = (
    <Space.Compact style={{ width: '100%' }}>
      {txtInput}
      <Badge dot={Object.values(i18nInput || {}).filter(Boolean).length > 0} color="blue">
        <Button icon={<Icon name="oct:globe" size={14} />} onClick={() => setOpen(true)} />
      </Badge>
    </Space.Compact>
  );

  return (
    <div className={className} style={style}>
      {title ? <div className={titleClassName}>{title}</div> : null}
      <div className={inputClassName}>
        {readonly ? value : i18nInput ? txtInputWithI18nInput : txtInput}
      </div>
      <Dialog
        style={{
          width: 1200,
          height: '80vh',
        }}
        open={open}
        title={t('i18n-picker.dialog-title')}
        onOk={() => {
          const draft = i18nKeySelectRef.current.getValue();
          if (onI18nChange) {
            const newI18nValue = Object.fromEntries(
              Object.entries(draft).map(([k, v]) => [`${id}_i18n_${k}`, v]),
            );
            onI18nChange(newI18nValue);
          } else {
            for (const [k, v] of Object.entries(draft)) {
              onChange(`${id}_i18n_${k}`, v);
            }
          }
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      >
        <I18nKeySelect
          {...i18nSelectorProps}
          ref={i18nKeySelectRef}
          defaultValue={i18nInput}
          fallbackText={valueProp}
        />
      </Dialog>
    </div>
  );
}

InputTextWithI18n.propTypes = {
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  i18nInput: PropTypes.shape({
    valueI18nKey: PropTypes.string,
    valueI18nNs: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onI18nChange: PropTypes.func,
  title: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  maxLength: PropTypes.number,
  emptyAsUndefined: PropTypes.bool,
  notAllowedRegex: PropTypes.instanceOf(RegExp),
  onFinishInput: PropTypes.func,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  i18nSelectorProps: PropTypes.shape(I18nKeySelect.propTypes),
};

export default InputTextWithI18n;
